import {z} from 'zod';

import {
  CustomFieldValueSchema,
  OrderPaymentResponseBody,
  OrderResponseBody,
  UuidResponseBody,
} from '@dms/api/shared';

// V3 response type - matches checkout module
export type OrderPaymentRecordPaidResponseBodyV3Read = {
  orderPayment: OrderPaymentResponseBody;
  transactions: OrderPaymentRecordPaidTransactionResponseBodyV3Read[];
};

export const OrderPaymentRecordPaidTransactionResponseBodyV3ReadSchema = z.object({
  transactionId: z.string(),
  documentId: z.string(),
  checkoutId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  paymentDirection: z.enum(['incoming', 'outgoing']),
});

export type OrderPaymentRecordPaidTransactionResponseBodyV3Read = z.infer<
  typeof OrderPaymentRecordPaidTransactionResponseBodyV3ReadSchema
>;

// Shared types
export const TenantBankAccountSchema = z.object({
  bankName: z.string().nullable(),
  accountName: z.string(),
  accountNumber: z.string(),
  isPrimary: z.boolean(),
  currencyCode: z.string().nullable(),
  swiftBic: z.string().nullable(),
  iban: z.string().nullable(),
});

export const EditPaymentRequestBodySchema = z.object({
  paymentMethod: z.enum(['CASH', 'BANK_TRANSFER', 'PAYMENT_CARD', 'OFFSET']),
  dueDate: z.string().nullable(),
  cashRegisterId: z.string().nullable(),
  customerBankAccountId: z.string().nullable(),
  tenantBankAccount: TenantBankAccountSchema.nullable(),
  amount: z.string().nullable(),
  comment: z.string().nullable(),
  exchangeRate: z.string().nullable(),
  supplierInvoiceNumber: z.string().nullable(),
  dateOfTaxableSupply: z.string().nullable(),
  createCashRegisterDocument: z.boolean().nullable(),
});

export const PaySaleBusinessCaseRequestBodySchema = z.object({
  payDate: z.string(),
  amount: z.string(),
  currency: z.string(),
  paymentMethod: z.enum(['CASH', 'BANK_TRANSFER', 'PAYMENT_CARD', 'OFFSET']),
  cashRegisterId: z.string().nullable().optional(),
  createCashRegisterDocument: z.boolean().nullable().optional(),
  tenantBankAccount: TenantBankAccountSchema.nullable().optional(),
});

export const IssuePaymentRequestBodySchema = z.object({
  issuedOn: z.string().nullable().optional(),
});

// New shared types for document creation
export const PaymentInfoRequestBodySchema = z.object({
  paymentMethod: z.enum(['BANK_TRANSFER', 'CARD', 'CASH', 'CASH_ON_DELIVERY', 'OFFSET']),
  bankAccount: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  iban: z.string().nullable().optional(),
  swift: z.string().nullable().optional(),
  variableSymbol: z.string().nullable().optional(),
  constantSymbol: z.string().nullable().optional(),
  specificSymbol: z.string().nullable().optional(),
});

export const InvoicingDocumentItemDiscountRequestBodySchema = z.object({
  relatedItemId: z.string(),
  percent: z.string(),
  originalPrice: z.string(),
  priceAfterDiscount: z.string(),
  discountValue: z.string(),
  currency: z.string(),
});

export const CorrectiveTaxDocumentItemRequestBodySchema = z.object({
  correctedItemId: z.string(),
  description: z.string(),
  quantity: z.string(),
  unit: z.string(),
  pricePerUnit: z.string(),
  isUnitPriceWithVat: z.boolean(),
  vatType: z.enum(['S', 'R', 'MR', 'E', 'Z', 'SR', 'P']),
  currency: z.string(),
  itemId: z.string(),
  type: z.enum(['standard', 'margin', 'discount']),
  relatedItemId: z.string().nullable(),
  discount: InvoicingDocumentItemDiscountRequestBodySchema.nullable().optional(),
});

export const AdditionalCustomerRequestBodySchema = z
  .object({
    customerId: z.string(),
    contractInformationId: z.string(),
    type: z.enum(['operator', 'recipient']),
  })
  .nullable();

export const TypedAnyValueSchema = z.object({
  type: z.enum(['integer', 'decimal', 'string', 'money', 'date', 'datetime', 'boolean']),
  value: z.union([
    z.array(z.any()),
    z.string(),
    z.number(),
    z.boolean(),
    z.object({
      amount: z.string(),
      currency: z.string(),
    }),
  ]),
});

export const ExchangeRateRatioBodySchema = z
  .object({
    amount: z.union([z.literal(1), z.literal(100), z.literal(1000)]),
    ratio: z.string(),
    currency: z.string(),
  })
  .nullable();

export const CorrectiveTaxDocumentRequestBodySchema = z.object({
  documentSeriesId: z.string(),
  invoicingDocumentId: z.string(),
  invoicingDocumentType: z.enum([
    'invoice',
    'balance_invoice',
    'invoice_proforma',
    'tax_document_for_payment',
    'invoice_draft',
    'corrective_tax_document',
    'cash_receipt',
  ]),
  issueDate: z.string(),
  dueDate: z.string(),
  dateOfTaxableSupply: z.string(),
  dateOfReception: z.string(),
  description: z.string(),
  reason: z.string(),
  behindItemsNote: z.string().nullable(),
  internalNote: z.string().nullable(),
  paymentInfo: PaymentInfoRequestBodySchema,
  items: z.array(CorrectiveTaxDocumentItemRequestBodySchema),
  additionalCustomer: AdditionalCustomerRequestBodySchema.optional(),
  customFields: z.array(CustomFieldValueSchema).optional(),
  exchangeRateRatio: ExchangeRateRatioBodySchema.optional(),
  caseNumber: z.string().nullable().optional(),
});

export const FileDataSchema = z.object({
  fileId: z.string(),
  name: z.string(),
  fileUri: z.string(),
  remoteId: z.string().nullable(),
  createdAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  documentType: z
    .enum([
      'PURCHASE_CONTRACT',
      'PURCHASE_POWER_OF_ATTORNEY',
      'PURCHASE_HANDOVER_PROTOCOL',
      'PURCHASE_BROKERAGE_HANDOVER_PROTOCOL',
      'SALE_OFFER',
      'PURCHASE_OFFER',
      'SALE_CONTRACT',
      'SALE_POWER_OF_ATTORNEY',
      'SALE_HANDOVER_PROTOCOL',
      'SALE_BROKERAGE_CONTRACT',
      'PAYMENT_ATTACHMENT',
      'PAYMENT_CASH_RECEIPT',
      'PAYMENT_CORRECTIVE_CASH_RECEIPT',
      'PAYMENT_INVOICE',
      'PAYMENT_PROFORMA_INVOICE',
      'PAYMENT_TAX_DOCUMENT',
      'PURCHASE_BROKERAGE_CONTRACT',
      'VEHICLE_CONDITION_PROTOCOL',
      'GDPR_MARKETING_CONSENT',
      'PURCHASE_BROKERAGE_POWER_OF_ATTORNEY',
      'CORRECTIVE_TAX_DOCUMENT',
      'OFFSETTING_OF_LIABILITIES_AND_RECEIVABLES',
      'INTERNAL_INVOICE_DOCUMENT',
      'INTERNAL_CORRECTIVE_TAX_DOCUMENT',
    ])
    .nullable(),
  fileFileId: z.string().nullable().optional(),
});

export const CreatePaymentFileRequestBodySchema = z.object({
  fileData: FileDataSchema,
});

export const CreateTaxDocumentRequestBodySchema = z.object({
  seriesId: z.string(),
});

export const CreateInternalInvoiceDocumentRequestBodySchema = z.object({
  templateId: z.string(),
});

export type TenantBankAccount = z.infer<typeof TenantBankAccountSchema>;
export type EditPaymentRequestBody = z.infer<typeof EditPaymentRequestBodySchema>;
export type IssuePaymentRequestBody = z.infer<typeof IssuePaymentRequestBodySchema>;
export type PaySaleBusinessCaseRequestBody = z.infer<typeof PaySaleBusinessCaseRequestBodySchema>;
export type PaymentInfoRequestBody = z.infer<typeof PaymentInfoRequestBodySchema>;
export type InvoicingDocumentItemDiscountRequestBody = z.infer<
  typeof InvoicingDocumentItemDiscountRequestBodySchema
>;
export type CorrectiveTaxDocumentItemRequestBody = z.infer<
  typeof CorrectiveTaxDocumentItemRequestBodySchema
>;
export type AdditionalCustomerRequestBody = z.infer<typeof AdditionalCustomerRequestBodySchema>;
export type TypedAnyValue = z.infer<typeof TypedAnyValueSchema>;
export type ExchangeRateRatioBody = z.infer<typeof ExchangeRateRatioBodySchema>;
export type CorrectiveTaxDocumentRequestBody = z.infer<
  typeof CorrectiveTaxDocumentRequestBodySchema
>;
export type FileData = z.infer<typeof FileDataSchema>;
export type CreatePaymentFileRequestBody = z.infer<typeof CreatePaymentFileRequestBodySchema>;
export type CreateTaxDocumentRequestBody = z.infer<typeof CreateTaxDocumentRequestBodySchema>;
export type CreateInternalInvoiceDocumentRequestBody = z.infer<
  typeof CreateInternalInvoiceDocumentRequestBodySchema
>;

// Purchase types
export const AddPurchasePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
});

export const CancelPurchasePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export const EditPurchasePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  editPaymentRequestBody: EditPaymentRequestBodySchema,
});

export const IssuePurchasePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  issuePaymentRequestBody: IssuePaymentRequestBodySchema,
  validateMandatoryFields: z.boolean().nullish().optional(),
});

export const PayPurchasePaymentV2ApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  paySaleBusinessCaseRequestBody: PaySaleBusinessCaseRequestBodySchema,
});

export const RemovePurchasePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export const WithdrawPurchasePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export type AddPurchasePaymentApiResponse = /** status 201  */ UuidResponseBody;
export type AddPurchasePaymentApiArg = z.infer<typeof AddPurchasePaymentApiArgSchema>;
export type CancelPurchasePaymentApiResponse = unknown;
export type CancelPurchasePaymentApiArg = z.infer<typeof CancelPurchasePaymentApiArgSchema>;
export type EditPurchasePaymentApiResponse = OrderResponseBody;
export type EditPurchasePaymentApiArg = z.infer<typeof EditPurchasePaymentApiArgSchema>;
export type IssuePurchasePaymentApiResponse = OrderPaymentResponseBody;
export type IssuePurchasePaymentApiArg = z.infer<typeof IssuePurchasePaymentApiArgSchema>;
export type PayPurchasePaymentV2ApiResponse = OrderPaymentRecordPaidResponseBodyV3Read;
export type PayPurchasePaymentV2ApiArg = z.infer<typeof PayPurchasePaymentV2ApiArgSchema>;

export type RemovePurchasePaymentApiResponse = unknown;
export type RemovePurchasePaymentApiArg = z.infer<typeof RemovePurchasePaymentApiArgSchema>;
export type WithdrawPurchasePaymentApiResponse = unknown;
export type WithdrawPurchasePaymentApiArg = z.infer<typeof WithdrawPurchasePaymentApiArgSchema>;

// Deposit types
export const AddSaleDepositPaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
});

export const CancelSaleDepositPaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export const EditSaleDepositPaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  editPaymentRequestBody: EditPaymentRequestBodySchema,
});

export const IssueSaleDepositPaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  issuePaymentRequestBody: IssuePaymentRequestBodySchema,
  validateMandatoryFields: z.boolean().nullish().optional(),
});

export const PaySaleDepositPaymentV2ApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  paySaleBusinessCaseRequestBody: PaySaleBusinessCaseRequestBodySchema,
});

export const RemoveSaleDepositPaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export const WithdrawSaleDepositPaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export const WithdrawPartialSaleDepositPaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  transactionId: z.string(),
});

export type AddSaleDepositPaymentApiResponse = /** status 201  */ UuidResponseBody;
export type AddSaleDepositPaymentApiArg = z.infer<typeof AddSaleDepositPaymentApiArgSchema>;

export type CancelSaleDepositPaymentApiResponse = unknown;
export type CancelSaleDepositPaymentApiArg = z.infer<typeof CancelSaleDepositPaymentApiArgSchema>;

export type EditSaleDepositPaymentApiResponse = OrderResponseBody;
export type EditSaleDepositPaymentApiArg = z.infer<typeof EditSaleDepositPaymentApiArgSchema>;

export type IssueSaleDepositPaymentApiResponse = OrderPaymentResponseBody;
export type IssueSaleDepositPaymentApiArg = z.infer<typeof IssueSaleDepositPaymentApiArgSchema>;

export type PaySaleDepositPaymentV2ApiResponse = OrderPaymentRecordPaidResponseBodyV3Read;
export type PaySaleDepositPaymentV2ApiArg = z.infer<typeof PaySaleDepositPaymentV2ApiArgSchema>;

export type RemoveSaleDepositPaymentApiResponse = unknown;
export type RemoveSaleDepositPaymentApiArg = z.infer<typeof RemoveSaleDepositPaymentApiArgSchema>;

export type WithdrawSaleDepositPaymentApiResponse = unknown;
export type WithdrawSaleDepositPaymentApiArg = z.infer<
  typeof WithdrawSaleDepositPaymentApiArgSchema
>;

export type WithdrawPartialSaleDepositPaymentApiResponse = OrderPaymentResponseBody;
export type WithdrawPartialSaleDepositPaymentApiArg = z.infer<
  typeof WithdrawPartialSaleDepositPaymentApiArgSchema
>;

// Balance types
export const CancelSaleBalancePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export const EditSaleBalancePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  editPaymentRequestBody: EditPaymentRequestBodySchema,
});

export const IssueSaleBalancePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  issuePaymentRequestBody: IssuePaymentRequestBodySchema,
  validateMandatoryFields: z.boolean().nullish().optional(),
});

export const PaySaleBalancePaymentV2ApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  paySaleBusinessCaseRequestBody: PaySaleBusinessCaseRequestBodySchema,
});

export const WithdrawSaleBalancePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export const WithdrawPartialSaleBalancePaymentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  transactionId: z.string(),
});

export type CancelSaleBalancePaymentApiResponse = unknown;
export type CancelSaleBalancePaymentApiArg = z.infer<typeof CancelSaleBalancePaymentApiArgSchema>;

export type EditSaleBalancePaymentApiResponse = OrderResponseBody;
export type EditSaleBalancePaymentApiArg = z.infer<typeof EditSaleBalancePaymentApiArgSchema>;

export type IssueSaleBalancePaymentApiResponse = OrderPaymentResponseBody;
export type IssueSaleBalancePaymentApiArg = z.infer<typeof IssueSaleBalancePaymentApiArgSchema>;

export type PaySaleBalancePaymentV2ApiResponse = OrderPaymentRecordPaidResponseBodyV3Read;
export type PaySaleBalancePaymentV2ApiArg = z.infer<typeof PaySaleBalancePaymentV2ApiArgSchema>;

export type WithdrawSaleBalancePaymentApiResponse = unknown;
export type WithdrawSaleBalancePaymentApiArg = z.infer<
  typeof WithdrawSaleBalancePaymentApiArgSchema
>;

export type WithdrawPartialSaleBalancePaymentApiResponse = OrderPaymentResponseBody;
export type WithdrawPartialSaleBalancePaymentApiArg = z.infer<
  typeof WithdrawPartialSaleBalancePaymentApiArgSchema
>;

// Document creation types
export const CreateFullCorrectiveTaxDocumentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  correctiveTaxDocumentRequestBody: CorrectiveTaxDocumentRequestBodySchema,
});

export const CreatePartialCorrectiveTaxDocumentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  correctiveTaxDocumentRequestBody: CorrectiveTaxDocumentRequestBodySchema,
});

export const CreatePaymentFileApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  createPaymentFileRequestBody: CreatePaymentFileRequestBodySchema,
});

export const CreateTaxDocumentApiArgSchema = z.object({
  businessCaseId: z.string(),
  transactionId: z.string(),
  createTaxDocumentRequestBody: CreateTaxDocumentRequestBodySchema,
});

export const CreateBrokerageInternalInvoiceDocumentDocumentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  createInternalInvoiceDocumentRequestBody: CreateInternalInvoiceDocumentRequestBodySchema,
});

export const CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  createInternalInvoiceDocumentRequestBody: CreateInternalInvoiceDocumentRequestBodySchema,
});

export const CreateInternalInvoiceDocumentDocumentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  createInternalInvoiceDocumentRequestBody: CreateInternalInvoiceDocumentRequestBodySchema,
});

export const CreateInternalInvoiceDocumentWithVatDocumentApiArgSchema = z.object({
  businessCaseId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  createInternalInvoiceDocumentRequestBody: CreateInternalInvoiceDocumentRequestBodySchema,
});

export type CreateFullCorrectiveTaxDocumentApiResponse = OrderPaymentResponseBody;
export type CreateFullCorrectiveTaxDocumentApiArg = z.infer<
  typeof CreateFullCorrectiveTaxDocumentApiArgSchema
>;

export type CreatePartialCorrectiveTaxDocumentApiResponse = OrderPaymentResponseBody;
export type CreatePartialCorrectiveTaxDocumentApiArg = z.infer<
  typeof CreatePartialCorrectiveTaxDocumentApiArgSchema
>;

export type CreatePaymentFileApiResponse = OrderPaymentResponseBody;
export type CreatePaymentFileApiArg = z.infer<typeof CreatePaymentFileApiArgSchema>;

export type CreateTaxDocumentApiResponse = UuidResponseBody;
export type CreateTaxDocumentApiArg = z.infer<typeof CreateTaxDocumentApiArgSchema>;

export type CreateBrokerageInternalInvoiceDocumentDocumentApiResponse = CheckoutResponseBodyRead;
export type CreateBrokerageInternalInvoiceDocumentDocumentApiArg = z.infer<
  typeof CreateBrokerageInternalInvoiceDocumentDocumentApiArgSchema
>;

export type CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiResponse =
  CheckoutResponseBodyRead;
export type CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiArg = z.infer<
  typeof CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiArgSchema
>;

export type CreateInternalInvoiceDocumentDocumentApiResponse = CheckoutResponseBodyRead;
export type CreateInternalInvoiceDocumentDocumentApiArg = z.infer<
  typeof CreateInternalInvoiceDocumentDocumentApiArgSchema
>;

export type CreateInternalInvoiceDocumentWithVatDocumentApiResponse = CheckoutResponseBodyRead;
export type CreateInternalInvoiceDocumentWithVatDocumentApiArg = z.infer<
  typeof CreateInternalInvoiceDocumentWithVatDocumentApiArgSchema
>;

export const OrderPaymentRecordPaidTransactionResponseBodyReadSchema = z.object({
  /** Created payment transaction id */
  transactionId: z.string(),
  /** Invoicing document id related to created transaction */
  documentId: z.string(),
  checkoutId: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
});

export type OrderPaymentRecordPaidTransactionResponseBodyRead = z.infer<
  typeof OrderPaymentRecordPaidTransactionResponseBodyReadSchema
>;

// Additional types needed for CheckoutResponseBodyRead
export const TaxDocumentSchema = z.object({
  taxDocumentId: z.string(),
  taxDocumentType: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  itemsContext: z.array(
    z.object({
      orderId: z.string().optional(),
      itemId: z.string().optional(),
      entityId: z.string().optional(),
      entityClass: z.string().optional(),
    })
  ),
  removed: z.boolean().optional(),
});

export const CheckoutResponseBodyReadSchema = z.object({
  id: z.string(),
  orders: z.array(z.any()), // OrderResponseBody from shared
  editContractInformationAllowed: z.boolean(),
  taxDocuments: z.array(TaxDocumentSchema),
});

export type TaxDocument = z.infer<typeof TaxDocumentSchema>;
export type CheckoutResponseBodyRead = z.infer<typeof CheckoutResponseBodyReadSchema>;
